<script>
  import { onMount } from 'svelte';
  // import GraphQL API url
  import { GRAPHQL_API_URL } from '../util/config';
  // import LoanForce post function
  import postToLoanForce from '../util/postToLoanForce';
  // import reCAPTCHA utility
  import { getRecaptchaToken } from '../util/recaptcha';
  // import and destructure validators and validation functions
  import { validators, validationFunctions } from '../util/validation';
  const {
    alphaNumericValidator,
    emailValidator,
    lengthValidator,
    telValidator,
  } = validators;
  const {
    addErrorsToFields,
    checkIfFieldsHaveErrors,
    clearFieldValues,
    validateFields,
  } = validationFunctions;

  // props provided from WordPress settings & PHP wp_localize_script (in setup.php)
  export let account;
  export let loanforceId;
  export let campaignPrefix;
  export let company;
  export let sourceName;
  export let brandButtonColor;

  // field data
  let firstName = {
    value: '',
    touched: false,
    error: false,
    errorMessage:
      'Please enter your first name using only letters, spaces, and numbers',
    validator: alphaNumericValidator,
  };
  let lastName = {
    value: '',
    touched: false,
    error: false,
    errorMessage:
      'Please enter your last name using only letters, spaces, and numbers',
    validator: alphaNumericValidator,
  };
  let email = {
    value: '',
    touched: false,
    error: false,
    errorMessage: 'Please enter a valid email address',
    validator: emailValidator,
  };
  let phone = {
    value: '',
    touched: false,
    error: false,
    errorMessage: 'Please enter a valid phone number',
    validator: telValidator,
  };
  let branchLocation = {
    value: '',
    touched: false,
    error: false,
    errorMessage: 'Please select a branch location',
    validator: lengthValidator,
  };
  let loanPurpose = {
    value: '',
    touched: false,
    error: false,
    errorMessage: 'Please select a loan purpose',
    validator: lengthValidator,
  };
  // currently disabled as text message option is not yet in API, re-enable this when it is
  // let viaText = {
  //   value: false,
  // };

  // do any fields have errors?
  let fieldsHaveErrors = true;

  // to keep track of POST request on form submission
  let isSending = false;
  let isSent = false;
  let isErrorSending = false;

  // height of form for ensuring container is same size when form is sent
  let formHeight;

    // branch locations data
  let branchLocations = [];
  let branchLocationsLoading = true;

  // gets list of branch locations via WordPress AJAX
  async function getBranchLocations() {
    const ajaxUrl = '/wp-admin/admin-ajax.php';
    
    try {
      const response = await fetch(ajaxUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: 'action=get_branch_locations'
      });
      
      const data = await response.json();
      
      if (data && data.length > 0) {
        branchLocations = data.map(branch => ({
          value: branch.title,
          name: branch.title
        }));
      } else {
        // Fallback options if no branches found
        branchLocations = [
          { value: 'Chesapeake, VA', name: 'Chesapeake, VA' },
          { value: 'Gulf Coast', name: 'Gulf Coast' },
          { value: 'Maitland, FL', name: 'Maitland, FL' },
          { value: 'Newport News, VA', name: 'Newport News, VA' },
          { value: 'Outer Banks, NC', name: 'Outer Banks, NC' },
          { value: 'Richmond, VA', name: 'Richmond, VA' },
          { value: 'Virginia Beach, VA', name: 'Virginia Beach, VA' }
        ];
      }
    } catch (error) {
      console.error('Failed to load branch locations:', error);
      
      // Fallback options on error
      branchLocations = [
        { value: 'Chesapeake, VA', name: 'Chesapeake, VA' },
        { value: 'Gulf Coast', name: 'Gulf Coast' },
        { value: 'Maitland, FL', name: 'Maitland, FL' },
        { value: 'Newport News, VA', name: 'Newport News, VA' },
        { value: 'Outer Banks, NC', name: 'Outer Banks, NC' },
        { value: 'Richmond, VA', name: 'Richmond, VA' },
        { value: 'Virginia Beach, VA', name: 'Virginia Beach, VA' }
      ];
    } finally {
      branchLocationsLoading = false;
    }
  }

  // mask for phone input - adds input mask in (###) ###-#### format
  // from https://stackoverflow.com/questions/17651207/mask-us-phone-number-string-with-javascript
  function addPhoneInputMask() {
    const phoneInput = phone.value
      .replace(/\D/g, '')
      .match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
    phone.value = !phoneInput[2]
      ? phoneInput[1]
      : '(' +
        phoneInput[1] +
        ') ' +
        phoneInput[2] +
        (phoneInput[3] ? '-' + phoneInput[3] : '');
  }

  onMount(() => {
    // get width of screen to determine what to set formHeight to
    const screenWidth = window.innerWidth;

    // set formHeight to clientHeight of form element
    if (screenWidth >= 1024) {
      formHeight =
        document.getElementById('schedule-a-consultation-form').clientHeight -
        200;
    } else {
      // success message wrapping div will be 300px
      formHeight = 300;
    }
    
    // Load branch locations
    getBranchLocations();
  });

  /**
   * Reactive statements to update field data
   * I am still figuring out how to work with these so there maybe room for improvement :) (as of 10/30/2019)
   **/

  // truncate phone number length if it has too many digits
  // works in conjunction with event listener in onMount() above to properly format phone number
  $: if (phone.value.length === 15) {
    phone.value = phone.value.substr(0, 14);
  }

  // checks if the field is valid on change - don't fire if the form has already been sent
  // without the !isSent conditional it'll geek out and try to validate after POSTing
  $: if (!isSent) {
    // this apparently has to be re-declared in every reactive statement
    const fields = [firstName, lastName, email, phone, branchLocation, loanPurpose];

    validateFields(fields);

    // keep track of if any fields have errors, used in submission function to stop submission if there are errors
    fieldsHaveErrors = checkIfFieldsHaveErrors(fields);
  }

  // on "Download Now" button click/form submission, add errors to fields that are blank or haven't been touched
  $: if (isSending && fieldsHaveErrors) {
    // if declared outside of reactive statements, they will not update the values
    const fields = [firstName, lastName, email, phone, branchLocation, loanPurpose];

    addErrorsToFields(fields);

    isSending = false;
  }

  // clear field values when form is successfully sent
  // tying this reactively to isSent makes using the clearFieldValues function viable, instead of targeting each field indivdually (see older commits for that)
  $: if (isSent) {
    // idk why lol Svelte is weird
    const fields = [
      firstName,
      lastName,
      email,
      phone,
      branchLocation,
      loanPurpose,
      // viaText,
    ];

    clearFieldValues(fields);

    // have to assign isSent to itself to trigger the reactive update?
    isSent = isSent;
  }

  // submit to LoanForce API
  async function submitHbGuideForm() {
    isSending = true;

    // any errors? don't send if so
    // this works in conjunction with the reactive (isSending && fieldsHaveErrors) to stop sending and add errors that might not be visible in the UI
    if (fieldsHaveErrors) {
      return;
    }

    try {
      // Get reCAPTCHA token
      const recaptchaToken = await getRecaptchaToken('schedule_consultation');
      
      // Verify reCAPTCHA token with WordPress
      const verifyResponse = await fetch('/wp-json/sanctum/v1/verify-recaptcha', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: recaptchaToken }),
      });
      
      const verifyResult = await verifyResponse.json();
      
      // If reCAPTCHA verification fails, show error and stop
      if (!verifyResult.success) {
        console.error('reCAPTCHA verification failed:', verifyResult);
        isErrorSending = true;
        isSending = false;
        return;
      }

      const dataToSend = {
        FirstName: firstName.value,
        LastName: lastName.value,
        Email: email.value,
        Phone: phone.value,
        LoanPurpose: loanPurpose.value,
        CampaignName: `${campaignPrefix} - Schedule a Consultation`,
        CampaignStatus: 'Active',
        Source: `${sourceName} - Sched a Consult - ${branchLocation.value}`,
        UserAccountName: `${account}`,
        Id: `${loanforceId}`,
        SyncWithMarketo: true,
        EmailOptIn: true,
      };

      const result = await fetch('https://social-media-server-414e76d9eb8b.herokuapp.com/api/websites/create-contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSend),
      });

      const apiResponse = await result.json();

      console.log(apiResponse)
      if (apiResponse.success === true) {
        isErrorSending = false;
        isSent = true;
        window.scroll({
          top: 0,
          left: 0,
          behavior: 'smooth',
        });
      } else {
        isErrorSending = true;
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      isErrorSending = true;
      isSending = false;
    }
  }
</script>

{#if isSent}
  <!-- keep this div the same height as the form so that the layout stays the same on submission (on >= lg screens - on smaller it is a consistent 300px) -->
  <div
    class="flex flex-col items-center justify-center text-center"
    style="height: {formHeight}px;">
    <h2 class="text-blue-primary text-22 mb-10 lg:mb-20 lg:text-35">
      Your request has been sent!
    </h2>
    <p class="text-18 lg:text-29">
      A mortgage specialist will be in touch with you soon.
    </p>
  </div>
{:else}
  <form
    id="schedule-a-consultation-form"
    on:submit|preventDefault={submitHbGuideForm}
    novalidate
    class="flex flex-col md:flex-row md:flex-wrap">
    <div class="form-field w-full md:w-1/2 md:pr-4">
      <input
        type="text"
        id="firstName"
        name="firstName"
        bind:value={firstName.value}
        class="form-input"
        required />
      <label for="firstName" class="form-label">First Name</label>
      {#if firstName.touched && firstName.error}
        <p class="text-red-error text-14 m-0 lg:text-15">
          {firstName.errorMessage}
        </p>
      {:else}
        <p class="text-14 m-0 lg:text-15">&nbsp;</p>
      {/if}
    </div>
    <div class="form-field w-full md:w-1/2 md:pl-4">
      <input
        type="text"
        id="lastName"
        name="lastName"
        bind:value={lastName.value}
        class="form-input"
        required />
      <label for="lastName" class="form-label">Last Name</label>
      {#if lastName.touched && lastName.error}
        <p class="text-red-error text-14 m-0 lg:text-15">
          {lastName.errorMessage}
        </p>
      {:else}
        <p class="text-14 m-0 lg:text-15">&nbsp;</p>
      {/if}
    </div>
    <div class="form-field w-full md:w-1/2 md:pr-4">
      <input
        type="text"
        id="email"
        name="email"
        bind:value={email.value}
        class="form-input"
        required />
      <label for="email" class="form-label">Email</label>
      {#if email.touched && email.error}
        <p class="text-red-error text-14 m-0 lg:text-15">
          {email.errorMessage}
        </p>
      {:else}
        <p class="text-14 m-0 lg:text-15">&nbsp;</p>
      {/if}
    </div>
    <div class="form-field w-full md:w-1/2 md:pl-4">
      <input
        type="text"
        id="phone"
        name="phone"
        bind:value={phone.value}
        on:input={addPhoneInputMask}
        class="form-input"
        required />
      <label for="phone" class="form-label">Phone</label>
      {#if phone.touched && phone.error}
        <p class="text-red-error text-14 m-0 lg:text-15">
          {phone.errorMessage}
        </p>
      {:else}
        <p class="text-14 m-0 lg:text-15">&nbsp;</p>
      {/if}
    </div>
    <div class="form-field w-full md:w-1/2 md:pr-4">
      <div class="form-select-wrapper">
        <select
          id="branchLocation"
          name="branchLocation"
          bind:value={branchLocation.value}
          class="form-select w-full"
          aria-label="Select nearest office location"
          disabled={branchLocationsLoading}
          required>
          {#if branchLocationsLoading}
            <option>Loading locations...</option>
          {:else}
            <option value="" selected disabled>Select nearest office location</option>
            {#each branchLocations as branch}
              <option value={branch.value}>
                {branch.name}
              </option>
            {/each}
          {/if}
        </select>
      </div>
      {#if branchLocation.touched && branchLocation.error}
        <p class="text-red-error text-14 m-0 mt-10 lg:text-15">
          {branchLocation.errorMessage}
        </p>
      {:else}
        <p class="text-14 m-0 mt-10 lg:text-15">&nbsp;</p>
      {/if}
    </div>
    <div class="form-field md:w-1/2 md:pl-4">
      <div class="form-select-wrapper">
        <select
          id="loanPurpose"
          name-="loanPurpose"
          bind:value={loanPurpose.value}
          class="form-select w-full"
          aria-label="Select a Loan Purpose"
          required>
          <option value="" selected disabled>Loan Purpose</option>
          <option value="purchase">Purchase</option>
          <option value="refinance">Refinance</option>
        </select>
      </div>
      {#if loanPurpose.touched && loanPurpose.error}
        <p class="text-red-error text-14 m-0 mt-10 lg:text-15">
          {loanPurpose.errorMessage}
        </p>
      {:else}
        <p class="text-14 m-0 mt-10 lg:text-15">&nbsp;</p>
      {/if}
    </div>
    <!-- <div class="form-field md:w-1/2 md:mt-10 md:pl-8">
      <p class="text-18">Receive communications via text?</p>
      <input
        type="checkbox"
        id="viaText"
        name="viaText"
        bind:checked={viaText.value}
        class="form-checkbox" />
      <label for="viaText">Yes, please send me communications via text</label>
      <p class="text-12 mt-5 lg:pr-32">
        Standard messaging and data rates may apply depending on the plan you
        have with your carrier.
      </p>
    </div> -->
    <div class="flex items-center justify-center md:-mt-8 mb-16 w-full lg:h-40">
      {#if !isSending}
        <div class="flex flex-col items-center">
          <div
            class="btn-outer {brandButtonColor ? 'brand-border' : 'border-red-button'}
            w-108 sm:w-160">
            <input
              type="submit"
              value="Schedule a Consultation"
              class="btn btn-action {brandButtonColor ? 'brand-background-color' : 'bg-red-button'}
              w-full" />
          </div>
          {#if isErrorSending}
            <p class="text-red-error text-18 text-center mt-6">
              Sorry, there was an error with your submission - please try again.
            </p>
          {/if}
        </div>
      {:else}
        <svg class="spinner h-40 w-40" viewbox="0 0 50 50">
          <circle class="path" cx="25" cy="25" r="20" />
        </svg>
      {/if}
    </div>
  </form>
  <p class="text-12">
    By clicking the ‘Schedule a Consultation’ button above, I authorize {company}
    to contact me using an automated dialing system, even if the telephone
    number is a cellular phone or other service for which the party is charged.
    I understand that I am not required to provide this consent in order to
    obtain goods or services from {company}. I agree to receive disclosures and
    communications in electronic form and I confirm that I have a working
    computer system and an internet website browser to view this webpage and
    this loan inquiry form. I agree that {company} may contact me by mail,
    telephone (mobile devices including SMS and MMS) or email in connection with
    my request even if my telephone number or email address appears on a {company}
    internal Do Not Call / Do Not Email List, a State or National Do Not Call
    Registry, a Do Not E-Mail list, or any other Do Not Contact List.
  </p>
{/if}



// WEBPACK FOOTER //
// ./scripts/svelte/ScheduleAConsultationForm.svelte